export const SCHEDULER_TABS_STORAGE_KEY = "selectTabs";

export const SCHEDULER_MAIN_TAB = {
  OFFICE: "1",
  MY_SCHEDULE: "2",
};

export const DEFAULT_SUBTAB = "1";

export const DEFAULT_SCHEDULER_TAB_STATE = {
  tab: SCHEDULER_MAIN_TAB.OFFICE,
  subTab: DEFAULT_SUBTAB,
  officeSubTab: DEFAULT_SUBTAB,
  myScheduleSubTab: DEFAULT_SUBTAB,
};

const OFFICE_SUBTABS = new Set(["1", "2"]);
const MY_SCHEDULE_SUBTABS = new Set(["1", "2", "3"]);

/**
 * @param {string} [value]
 * @returns {string}
 */
export const resolveOfficeSubTab = (value) =>
  OFFICE_SUBTABS.has(value) ? value : DEFAULT_SUBTAB;

/**
 * @param {string} [value]
 * @returns {string}
 */
export const resolveMyScheduleSubTab = (value) =>
  MY_SCHEDULE_SUBTABS.has(value) ? value : DEFAULT_SUBTAB;

/**
 * Normalizes persisted scheduler tab state.
 * Supports legacy `{ tab, subTab }` and the per-main-tab shape.
 *
 * @param {object | null | undefined} raw
 * @returns {{
 *   tab: string,
 *   subTab: string,
 *   officeSubTab: string,
 *   myScheduleSubTab: string,
 * }}
 */
export const normalizeSchedulerTabState = (raw) => {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_SCHEDULER_TAB_STATE };
  }

  const tab =
    raw.tab === SCHEDULER_MAIN_TAB.MY_SCHEDULE
      ? SCHEDULER_MAIN_TAB.MY_SCHEDULE
      : SCHEDULER_MAIN_TAB.OFFICE;

  const officeSubTab = resolveOfficeSubTab(
    raw.officeSubTab ??
      (raw.tab === SCHEDULER_MAIN_TAB.OFFICE ? raw.subTab : undefined),
  );
  const myScheduleSubTab = resolveMyScheduleSubTab(
    raw.myScheduleSubTab ??
      (raw.tab === SCHEDULER_MAIN_TAB.MY_SCHEDULE ? raw.subTab : undefined),
  );

  return {
    tab,
    officeSubTab,
    myScheduleSubTab,
    subTab:
      tab === SCHEDULER_MAIN_TAB.MY_SCHEDULE ? myScheduleSubTab : officeSubTab,
  };
};

/**
 * Reads scheduler tab selection from localStorage.
 *
 * @returns {{
 *   tab: string,
 *   subTab: string,
 *   officeSubTab: string,
 *   myScheduleSubTab: string,
 * }}
 */
export const readSchedulerTabState = () => {
  try {
    const cached = localStorage.getItem(SCHEDULER_TABS_STORAGE_KEY);
    return normalizeSchedulerTabState(cached ? JSON.parse(cached) : null);
  } catch {
    return { ...DEFAULT_SCHEDULER_TAB_STATE };
  }
};

/**
 * Switches the main tab while keeping each tab's last subtab.
 *
 * @param {object} prev
 * @param {string} activeTab
 */
export const withActiveMainTab = (prev, activeTab) => {
  const normalized = normalizeSchedulerTabState(prev);
  const tab =
    activeTab === SCHEDULER_MAIN_TAB.MY_SCHEDULE
      ? SCHEDULER_MAIN_TAB.MY_SCHEDULE
      : SCHEDULER_MAIN_TAB.OFFICE;

  if (normalized.tab === tab) {
    return normalized;
  }

  return {
    ...normalized,
    tab,
    subTab:
      tab === SCHEDULER_MAIN_TAB.MY_SCHEDULE
        ? normalized.myScheduleSubTab
        : normalized.officeSubTab,
  };
};

/**
 * Records the Office Schedule subtab without dropping My Schedule selection.
 *
 * @param {object} prev
 * @param {string} officeSubTab
 */
export const withOfficeSubTab = (prev, officeSubTab) => {
  const normalized = normalizeSchedulerTabState(prev);
  const next = resolveOfficeSubTab(officeSubTab);

  return {
    ...normalized,
    tab: SCHEDULER_MAIN_TAB.OFFICE,
    subTab: next,
    officeSubTab: next,
  };
};

/**
 * Records the My Schedule subtab without dropping Office Schedule selection.
 *
 * @param {object} prev
 * @param {string} myScheduleSubTab
 */
export const withMyScheduleSubTab = (prev, myScheduleSubTab) => {
  const normalized = normalizeSchedulerTabState(prev);
  const next = resolveMyScheduleSubTab(myScheduleSubTab);

  return {
    ...normalized,
    tab: SCHEDULER_MAIN_TAB.MY_SCHEDULE,
    subTab: next,
    myScheduleSubTab: next,
  };
};
